import _ from 'lodash'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state'

import { List, ListItem, Typography, Paper, IconButton, Popover, Tooltip, Badge, Divider, Stack, Box } from '@mui/material'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import PatientIcon from '@/components/ui/icons/Patient'
import PdsIcon from '@/components/ui/icons/Pds'

import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { WaitedTime } from '@/pages/waiting-room/ui/WaitingConsultationsTable'
import TopbarButton from '../../../layouts/diagnostic/TopbarButton'

type WaitingPatientsMiniViewProps = {
  waitingRoomId: string
}

export function WaitingPatientsMiniView({ waitingRoomId }: WaitingPatientsMiniViewProps) {
  const { t } = useTranslation()

  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId, { refetchOnMountOrArgChange: true })

  const consultations = useMemo(() => {
    const consultations = waitingRoom?.consultations ?? []

    return consultations.map((consultation) => {
      return {
        _id: consultation._id,
        patient: consultation.patient,
        pds: consultation.pds,
        startDate: consultation.startDate,
      }
    })
  }, [waitingRoom])

  return (
    <PopupState variant="popover" popupId="patient-source-menu">
      {(popupState) => (
        <>
          <Tooltip title={t('page.waitingRoom.modal.title')} placement="left-start" arrow>
            <TopbarButton {...bindTrigger(popupState)}>
              <Badge badgeContent={consultations.length} color="secondary">
                <PeopleAltIcon htmlColor="#FFF" />
              </Badge>
            </TopbarButton>
          </Tooltip>
          <Popover
            {...bindPopover(popupState)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Paper
              elevation={3}
              sx={{
                width: 'auto',
                minWidth: 300,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <Typography variant="subtitle1" sx={{ color: 'grey', p: '8px 16px' }}>
                {t('page.waitingRoom.modal.patients_count', { count: consultations.length })}
              </Typography>
              <Divider />
              <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {consultations.map((c) => (
                  <ListItem key={c._id}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '16px',
                        width: '100%',
                      }}
                    >
                      <Stack direction="row" spacing={1}>
                        <PatientIcon fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {c.patient?.userInfo?.name} {c.patient?.userInfo?.surname}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <PdsIcon fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {c.pds?.userInfo.name} {c.pds?.userInfo.surname}
                        </Typography>
                      </Stack>
                      <WaitedTime startDate={c.startDate} />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popover>
        </>
      )}
    </PopupState>
  )
}

export default WaitingPatientsMiniView
